import { z } from 'zod';

// ── Insight flag ──────────────────────────────────────────────────────────────
const insightFlagSchema = z.object({
  active: z.boolean(),
  age: z.number().int().nonnegative(),
});

// SA-4 fix: all four keys optional — 1,064 of 2,000 customers have empty {}
const insightSchema = z.object({
  dormant: insightFlagSchema.optional(),
  flight: insightFlagSchema.optional(),
  smb: insightFlagSchema.optional(),
  relgap: insightFlagSchema.optional(),
});

// SA-6 fix: nullable — ~70% of customers have no campaign history
const lastCampaignSchema = z.object({
  name: z.string(),
  daysAgo: z.number().int().nonnegative(),
});

// ── Customer ──────────────────────────────────────────────────────────────────
export const customerSchema = z.object({
  id: z.string(),
  name: z.string(),
  tier: z.enum(['Tier 1', 'Tier 2', 'Tier 3']),
  ageMo: z.number().int().nonnegative(),
  balance: z.number(),
  accountType: z.enum(['Personal', 'Business']),
  holdings: z.array(z.string()),
  credit: z.number().int().nullable(),
  homeowner: z.boolean(),
  insights: insightSchema,
  lastCampaign: lastCampaignSchema.nullable(),
  branch: z.string(),
  region: z.string(),
  // Immutable natural key (SCRUM-35); shown in the Campaigns drawer header + CSV
  // "portfolio" column (SCRUM-204). Always present in the API response.
  portfolioId: z.string(),
});

export type Customer = z.infer<typeof customerSchema>;

// ── API response wrapper ──────────────────────────────────────────────────────
export const customersResponseSchema = z.array(customerSchema);

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Derives a customer tier from balance, tenure, and product breadth.
 * Q3 resolution from the SCRUM-56/68 plan.
 */
export function deriveTier(c: Customer): 'Tier 1' | 'Tier 2' | 'Tier 3' {
  const score =
    (c.balance > 100_000 ? 2 : c.balance > 10_000 ? 1 : 0) +
    (c.ageMo > 120 ? 2 : c.ageMo > 36 ? 1 : 0) +
    (c.holdings.length >= 3 ? 2 : c.holdings.length >= 2 ? 1 : 0);
  return score >= 5 ? 'Tier 1' : score >= 3 ? 'Tier 2' : 'Tier 3';
}
